import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './use-data';

import { dataService } from '@/services/data-service';
import { APPROVE_CHUNK_CAP } from '@/types/schemas/work-order-review';
import { type ApprovalSlaStatus } from '@/utils/approval-sla';

/**
 * Client hooks for the review → approval workflow (#496).
 *
 * ── WHY NOT `useAccountingMutation` ──
 *
 * These transitions straddle two query trees. `useAccountingMutation` invalidates
 * `['accounting']`, which covers the review queue but NOT the PM's dashboard card or the
 * work-order detail — both of which live under `['workOrders']`. A reviewer marking a work
 * order ready and then seeing it still sitting in the Needs Review tab (or a PM approving
 * and watching the row stay put) is the whole class of bug this invalidation exists to
 * prevent, so both trees are cleared on every one of them.
 */

function useInvalidateReview() {
	const qc = useQueryClient();
	return useCallback(async () => {
		await Promise.all([
			qc.invalidateQueries({ queryKey: ['accounting'] }),
			qc.invalidateQueries({ queryKey: queryKeys.workOrders.all }),
		]);
	}, [qc]);
}

/** Accounting has written the customer-facing description; hand it to the PM. */
export function useMarkReadyForApproval() {
	const invalidate = useInvalidateReview();
	return useMutation({
		mutationFn: ({ workOrderId, customerDescription }: {
			workOrderId: string;
			customerDescription?: string;
		}) => dataService.markWorkOrderReadyForApproval(workOrderId, { customerDescription }),
		onSuccess: () => invalidate(),
	});
}

/** The PM sends it back with a reason. */
export function useReturnForReview() {
	const invalidate = useInvalidateReview();
	return useMutation({
		mutationFn: ({ workOrderId, note }: { workOrderId: string; note: string }) =>
			dataService.returnWorkOrderForReview(workOrderId, note),
		onSuccess: () => invalidate(),
	});
}

/**
 * The PM approves ONE work order from its detail page.
 *
 * The single-approve write (a `pmApproved` update), not the bulk route, so the history row
 * reads as an ordinary approval rather than "Approved (bulk)". Routed through here for the
 * invalidation: the detail page used to write through `dataService` directly, which left
 * the dashboard's To Do list and the Needs Approval badge showing the row it had just cleared.
 */
export function useApproveWorkOrder() {
	const invalidate = useInvalidateReview();
	return useMutation({
		mutationFn: ({ workOrderId, approvedBy }: { workOrderId: string; approvedBy?: string }) =>
			dataService.updateWorkOrder(workOrderId, { pmApproved: Date.now(), approvedBy }),
		onSuccess: () => invalidate(),
	});
}

/** One chunk of a bulk approve. Prefer {@link useBulkApproveAll} unless you are looping. */
export function useBulkApproveWorkOrders() {
	const invalidate = useInvalidateReview();
	return useMutation({
		mutationFn: (workOrderIds: string[]) => dataService.bulkApproveWorkOrders(workOrderIds),
		onSuccess: () => invalidate(),
	});
}

export interface BulkApproveTotals {
	approved: number;
	skipped: number;
	errors: Array<{ workOrderId: string; error: string; code?: string }>;
}

/**
 * Approve any number of work orders, chunked under the server's cap.
 *
 * Iterates the SELECTION rather than re-reading a server-side count. A work order that
 * fails permanently — say a PM's stale tab holding one from an association reassigned away
 * — stays selectable forever, so looping until some remote queue empties would never
 * terminate. Every submitted id comes back in `approved`, `skipped` or `errors`.
 *
 * Each POST is short, bounded and complete before the next begins: the shape every bulk
 * path in this app uses, and the only one that survives `wrangler dev`.
 */
export function useBulkApproveAll() {
	const chunk = useBulkApproveWorkOrders();

	const approveAll = useCallback(async (
		workOrderIds: string[],
		onProgress?: (done: number, total: number) => void,
	): Promise<BulkApproveTotals> => {
		const totals: BulkApproveTotals = { approved: 0, skipped: 0, errors: [] };
		const ids = [...workOrderIds];

		for (let i = 0; i < ids.length; i += APPROVE_CHUNK_CAP) {
			const slice = ids.slice(i, i + APPROVE_CHUNK_CAP);
			const result = await chunk.mutateAsync(slice);
			totals.approved += result.approved;
			totals.skipped += result.skipped;
			totals.errors.push(...result.errors);
			onProgress?.(Math.min(i + slice.length, ids.length), ids.length);
		}
		return totals;
	}, [chunk]);

	return { approveAll, isPending: chunk.isPending };
}

export type { ApprovalSlaStatus };
